/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import autoTable, { Color, RowInput } from 'jspdf-autotable'
import { CellHookData, Table, UserOptions } from 'jspdf-autotable'

/**************************************************************************************************/
/** @import Helpers/Documents                                                                    **/
/**************************************************************************************************/
import { PDFRectangle } from '@/Helpers/Documents/LegacyPDF/GeometryPDF'
import { LegacyPDF } from '@/Helpers/Documents/LegacyPDF/LegacyPDF'

/**************************************************************************************************/
/** @internal Type @name HorizontalAlign                                                         **/
/**************************************************************************************************/
type HorizontalAlign = 'left' | 'center' | 'right'

/**************************************************************************************************/
/** @internal Type @name VerticalAlign                                                           **/
/**************************************************************************************************/
type VerticalAlign = 'top' | 'middle' | 'bottom'

/**************************************************************************************************/
/** @export Class @name TablePDF                                                                 **/
/**************************************************************************************************/
export class TablePDF
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	private pdf: LegacyPDF
	private activeColId!: string
	private cols: Array<TableColumnPDF> = []
	private footerX = 0
	private footerY = 0
	private headStyles: Array<{ style: string, value: any }> = []
	private rows: Array<TableRowPDF> = []
	private updateGlobalY = true
	private x: number
	private y: number

	// Propiedades Generales.
	private tableOptions: UserOptions = {
		bodyStyles: { valign: 'middle' },
		columnStyles: {},
		headStyles: { valign: 'middle' },
		margin: { top: 28, right: 0, bottom: 0, left: 0 },
		showHead: 'everyPage',
		showFoot: 'never',
		styles: {},
		tableWidth: 'wrap',
		theme: 'plain'
	}

	/*************************************************/
	/** @Constructor                                **/
	/*************************************************/
	constructor(pdf: LegacyPDF, x: number, y: number, cols?: Array<TableColumnPDF>, rows?: Array<TableRowPDF>, theme = 'default')
	{
		this.pdf = pdf
		this.x = x
		this.y = y

		// Data para generar el contenido de la tabla.
		if (cols) this.cols = cols
		if (rows) this.rows = rows

		// Incluir la posición X como el margen izquierdo.
		const options: any = this.tableOptions
		options.startY = this.pdf.getGlobalY() + this.y
		this.setMargin('left', x)

		// Establecer el Theme (estilos predeterminados).
		this._applyTableTheme(theme)
	}

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	private _afterTableDrawn() : this
	{
		this.tableOptions.didDrawPage = (data) =>
		{
			// Actualizar la Posición Global Y.
			const tableHeight = this.getTableHeight(data.table)
			if (this.updateGlobalY) this.pdf.updateGlobalY(tableHeight)

			// Actualizar el Espacio Global disponible de la página.
			this.pdf.updateAvailableHeightSpace(tableHeight, true)
		}

		return this
	}

	private _getActiveColId() : string
	{
		return this.activeColId
	}

	private _applyTableTheme(theme: string) : void
	{
		switch (theme)
		{
			case 'default':
				this.setBodyStyle('lineColor', '#DDDDDD')
					.setBodyStyle('lineWidth', 1)
					.setBodyStyle('textColor', '#345A5A')
					.setHeaderStyle('fillColor', '#FBFBFB')
					.setHeaderStyle('halign', 'center')
					.setHeaderStyle('lineColor', '#DDDDDD')
					.setHeaderStyle('lineWidth', 1)
					.setHeaderStyle('textColor', '#345A5A')
					.setWidth('auto')
				; break;
		}
	}

	private _getColumnStylesById(id: string) : any
	{
		const c = this.tableOptions.columnStyles![id]
		if (!c) this.tableOptions.columnStyles![id] = {}
		return this.tableOptions.columnStyles![id]
	}

	private _getCurrentColumnStyles() : any
	{
		const id = this._getActiveColId()
		return this._getColumnStylesById(id)
	}

	private _verifyIfNewPageWasCreated() : void
	{
		const lastTable = this.pdf.getLastTable()
		if (lastTable.pageNumber !== this.pdf.getCurrentPage())
		{
			this.pdf.setCurrentPage(lastTable.pageNumber)
			this.pdf.setGlobalY(this.pdf.getLastTableY())
			this.pdf.setAvailableHeightSpace(this.pdf.getPageHeight() - lastTable.finalY! - 21.5)
		}
	}

	public applyHeadStyles() : this
	{
		this.onCellParsed((data) =>
		{
			if (data.section === 'head')
			{
				if (data.column.dataKey === this._getActiveColId())
				{
					for (const h of this.headStyles)
					{
						const { styles }: any = data.cell
						styles[h.style] = h.value
					}
					this.headStyles = []
				}
			}
		})
		return this
	}

	public colAlign(h: HorizontalAlign, v: VerticalAlign) : this
	{
		this.colAlignH(h).colAlignV(v)
		return this
	}

	public colAlignH(align: HorizontalAlign) : this
	{
		const column = this._getCurrentColumnStyles()
		column.halign = align
		return this
	}

	public colAlignV(align: VerticalAlign) : this
	{
		const column = this._getCurrentColumnStyles()
		column.valign = align
		return this
	}

	public colFillColor(c: Color) : this
	{
		const column = this._getCurrentColumnStyles()
		column.fillColor = c
		return this
	}

	public colFontStyle(style: string) : this
	{
		const column: any = this._getCurrentColumnStyles()
		column.fontStyle = style
		return this
	}

	public colTextColor(c: Color) : this
	{
		const column = this._getCurrentColumnStyles()
		column.textColor = c
		return this
	}

	public colWidth(w: 'auto' | 'wrap' | number) : this
	{
		const column = this._getCurrentColumnStyles()
		column.cellWidth = w
		return this
	}

	public draw(preventDefaults = false) : void
	{
		// Función Core para generar Tablas.
		const options = this.tableOptions
		const doc = this.pdf.getGlobalDoc()

		// La data de las Columnas y Filas debe ir incluido en el mismo objeto de opciones.
		if (!preventDefaults)
		{
			options.body = this.rows
			options.columns = this.cols.map((c) => { return { dataKey: c.key, header: c.label } })
		}

		// Ejecutar el Hook que actualizará el GlobalY.
		this._afterTableDrawn()

		// Generar la tabla en el documento de jsPDF.
		autoTable(doc, options)

		// Actualizar la Posición Global Y.
		if (this.updateGlobalY) this.pdf.updateGlobalY(this.y)

		// Verificar si la tabla generó una nueva pagina, la posición Y debe ser restablecida.
		this._verifyIfNewPageWasCreated()
	}

	public drawFooter(pdf: LegacyPDF, text: string, align = 'right') : void
	{
		new PDFRectangle(pdf, 0, this.footerY, pdf.getPageWidth(), 24)
			.setIncludeHeight(false)
			.setColor('#FBFBFB')
			.setStrokeColor('#DDDDDD')
			.draw()
		;

		const baseX = (pdf.getPageWidth() - 8) + this.footerX
		pdf.updateTextOption('align', align).setFont('OpenSans-Normal').setFontColor('#345A5A')
		pdf.addText(text, baseX, 7, 10)
	}

	public getMargins() : any
	{
		const margins: any = this.tableOptions.margin
		return margins
	}

	public getPDF() : LegacyPDF
	{
		return this.pdf
	}

	public getTableHeight(table: Table) : number
	{
		// Variable con la posición Y que se debe incluir a la posición Global Y.
		let y = 0

		// Solamente incluir el 'header' si este se esta mostrando.
		const { showHead } = this.tableOptions
		if (showHead !== 'never') y += table.head[0].height

		// Solamente incluir el 'footer' si este se esta mostrando.
		const { showFoot } = this.tableOptions
		if (showFoot !== 'never' && table.foot.length > 0) y += table.foot[0].height

		// Incrementar la variable 'y' con el alto de cada fila.
		for (const r of table.body) y += r.height
		return y
	}

	public getX() : number
	{
		return this.x
	}

	public getY() : number
	{
		return this.y
	}

	public headAlign(h: HorizontalAlign, v: VerticalAlign) : this
	{
		this.headAlignH(h).headAlignV(v)
		return this
	}

	public headAlignH(align: HorizontalAlign) : this
	{
		this.headStyles.push({ style: 'halign', value: align })
		return this
	}

	public headAlignV(align: VerticalAlign) : this
	{
		this.headStyles.push({ style: 'valign', value: align })
		return this
	}

	public onCellBeforeDraw(callback: (hookData: CellHookData) => void) : this
	{
		// Hook: Called before a cell or row is drawn. Can be used to call native jspdf styling functions such as doc.setTextColor or change
		// position of text etc before it is drawn.
		this.tableOptions.willDrawCell = callback
		return this
	}

	public onCellDrawn(callback: (hookData: CellHookData) => void) : this
	{
		// Hook: Called after a cell has been added to the page. Can be used to draw additional cell content such as images with doc.addImage,
		// additional text with doc.addText or other jspdf shapes.
		this.tableOptions.didDrawCell = callback
		return this
	}

	public onCellParsed(callback: (hookData: CellHookData) => void) : this
	{
		// Hook: Called when the plugin finished parsing cell content. Can be used to override content or styles for a specific cell.
		this.tableOptions.didParseCell = callback
		return this
	}

	public setActiveColId(id: string) : this
	{
		this.activeColId = id
		return this
	}

	public setBody(body: Array<RowInput>) : this
	{
		this.tableOptions.body = body
		return this
	}

	public setBodyStyle(sName: string, sValue: any) : this
	{
		const bodyStyles: any = this.tableOptions.bodyStyles
		bodyStyles[sName] = sValue
		return this
	}

	public setFoot(foot: Array<RowInput>) : this
	{
		this.tableOptions.foot = foot
		return this
	}

	public setFooterX(x: number) : this
	{
		this.footerX = x
		return this
	}

	public setFooterY(y: number) : this
	{
		this.footerY = y
		return this
	}

	public setHead(head: Array<RowInput>) : this
	{
		this.tableOptions.head = head
		return this
	}

	public setHeaderStyle(sName: string, sValue: any) : this
	{
		const headStyles: any = this.tableOptions.headStyles
		headStyles[sName] = sValue
		return this
	}

	public setMargin(side: string, val: number, includePageMargin = true) : this
	{
		const options: any = this.tableOptions
		const pageMargin = this.pdf.getPageMargin()
		options.margin[side] = val + (includePageMargin ? pageMargin : 0)
		return this
	}

	public setStyle(sName: string, sValue: any) : this
	{
		const styles: any = this.tableOptions.styles
		styles[sName] = sValue
		return this
	}

	public setTheme(theme: 'striped' | 'grid' | 'plain') : this
	{
		this.tableOptions.theme = theme
		return this
	}

	public setUpdateGlobalY(bool: boolean) : this
	{
		this.updateGlobalY = bool
		return this
	}

	public setWidth(w: 'auto' | 'wrap' | number) : this
	{
		// Comprobar si el ancho es un numero.
		if (typeof w === 'number')
		{
			this.tableOptions.tableWidth = w
		}
		else
		{
			this.tableOptions.tableWidth = w === 'auto' ? this.pdf.getPageWidth() : w
		}
		return this
	}

	public setY(y: number) : this
	{
		this.tableOptions.startY = y
		return this
	}

	public showHeader(show: 'always' | 'once' | false) : this
	{
		let showHead: any = null
		switch (show)
		{
			case 'always': showHead = 'everyPage'; break
			case 'once': showHead = 'firstPage'; break
			case false: showHead = 'never'; break
		}
		this.tableOptions.showHead = showHead
		return this
	}
}

/**************************************************************************************************/
/** @export Function @name TableExtraMarginOnNewPageCreation                                     **/
/**************************************************************************************************/
export function TableExtraMarginOnNewPageCreation(pdf: LegacyPDF, data: CellHookData, firstColumnKey: string, offset: number) : void
{
	if (pdf.getCurrentPage() < data.pageNumber)
	{
		if (data.section === 'head' && data.column.dataKey === firstColumnKey)
		{
			data.cursor!.y += offset
			data.cell.y += offset
		}
	}
}

/**************************************************************************************************/
/** @export Function @name TableSetSpecificBodyCellStyle                                         **/
/**************************************************************************************************/
export function TableSetSpecificBodyCellStyle(data: CellHookData, columnKey: string, styleProp: string, styleValue: any) : void
{
	if (data.section === 'body' && data.column.dataKey === columnKey)
	{
		const styles: any = data.cell.styles
		styles[styleProp] = styleValue
	}
}

/**************************************************************************************************/
/** @export Interface @name TableColumnPDF                                                       **/
/**************************************************************************************************/
export interface TableColumnPDF
{
	key: string
	label: string
}

/**************************************************************************************************/
/** @export Interface @name TableRowPDF                                                          **/
/**************************************************************************************************/
export interface TableRowPDF
{
	[key: string]: number | string
}